export type AnyRecord = Record<string, unknown>;

export interface SessionUser {
  id: string;
  publicId: string;
  displayName: string;
  email: string;
  locale: "ar" | "en";
  type: string;
  permissions: string[];
  roles: string[];
}

export interface ApiMeta {
  requestId?: string;
  pagination?: {
    nextCursor?: string | null;
    hasMore?: boolean;
  };
  [key: string]: unknown;
}

export interface ApiSuccess<T> {
  ok: true;
  data: T;
  meta: ApiMeta;
}

export interface ApiFailure {
  ok: false;
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
  meta?: Pick<ApiMeta, "requestId">;
}

export type ApiEnvelope<T> = ApiSuccess<T> | ApiFailure;

export const isRecord = (value: unknown): value is AnyRecord => typeof value === "object" && value !== null && !Array.isArray(value);

export function stringValue(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "bigint") return String(value);
  return fallback;
}

export function booleanValue(value: unknown): boolean {
  return value === true || value === "true" || value === 1;
}

export function listFromPayload(payload: unknown): AnyRecord[] {
  if (Array.isArray(payload)) return payload.filter(isRecord);
  if (!isRecord(payload)) return [];
  const candidates = [payload["items"], payload["results"], payload["rows"], payload["data"]];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate.filter(isRecord);
  }
  return [];
}

export function objectFromPayload(payload: unknown): AnyRecord {
  return isRecord(payload) ? payload : {};
}
